// Marketing events of the public pages — an integration point, not a tracker.
//
// zBilling has no analytics provider installed (no GA4, GTM, Meta pixel…), so
// nothing here sends data anywhere. Each event is:
//   1. pushed to window.dataLayer, but ONLY if a tag manager created it
//      (Google Tag Manager does; until one is installed this does nothing);
//   2. dispatched as a `zb:track` DOM event, for any future listener.
// When a provider is added, it must sit behind the cookie-consent banner
// (Consent Mode in the EU) and read these events — see docs/ANALYTICS_EVENTS.md.
//
// Never pass personal, fiscal or invoice data: event names and short,
// non-identifying labels only (where on the page, which plan, which tool).
export const EVENTS = Object.freeze({
  SIGN_UP_CTA_CLICK: 'sign_up_cta_click',
  PRICING_VIEW: 'pricing_view',
  CALCULATOR_USED: 'calculator_used',
  VERIFACTU_CTA_CLICK: 'verifactu_cta_click',
  COMPARISON_CTA_CLICK: 'comparison_cta_click',
});

const KNOWN = new Set(Object.values(EVENTS));
// Only short labels: anything that looks like an e-mail, a tax ID or a long
// free text is dropped before it can leave this function.
const SAFE_VALUE = /^[a-z0-9_\-/.:]{1,60}$/i;

export function cleanProps(props = {}) {
  const out = {};
  for (const [key, value] of Object.entries(props)) {
    if (!/^[a-z_]{1,30}$/.test(key)) continue;
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
    else if (typeof value === 'string' && SAFE_VALUE.test(value) && !/@/.test(value)) out[key] = value;
  }
  return out;
}

export function track(event, props = {}, win = typeof window !== 'undefined' ? window : null) {
  if (!win || !KNOWN.has(event)) return null;
  const payload = { event, ...cleanProps(props) };
  try {
    if (Array.isArray(win.dataLayer)) win.dataLayer.push(payload);
    win.dispatchEvent(new win.CustomEvent('zb:track', { detail: payload }));
  } catch { /* never break the page for a measurement */ }
  return payload;
}

// One listener for the whole document: any element (or its ancestor) with
// data-track="<event>" reports a click, with data-track-location as label.
// Works for the prerendered HTML and the live page alike, no wiring needed.
export function installClickTracking(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || doc.__zbTracking) return;
  doc.__zbTracking = true;
  doc.addEventListener('click', (e) => {
    const el = e.target && e.target.closest ? e.target.closest('[data-track]') : null;
    if (!el) return;
    track(el.getAttribute('data-track'), {
      location: el.getAttribute('data-track-location') || undefined,
      path: doc.location ? doc.location.pathname : undefined,
    }, doc.defaultView);
  }, { capture: true });
}

// Reports `event` once, the first time `el` is at least half visible.
export function trackViewOnce(el, event, props = {}) {
  if (!el || typeof window === 'undefined' || !('IntersectionObserver' in window)) return () => {};
  const io = new window.IntersectionObserver((entries) => {
    if (entries.some((x) => x.isIntersecting)) {
      track(event, props);
      io.disconnect();
    }
  }, { threshold: 0.5 });
  io.observe(el);
  return () => io.disconnect();
}
